<template>
    <vx-chart-record
        :loading="loading"
        :error="Boolean(error)"
        :data="dataComputed || []"
        @item:click="($event, item) => emit('resource:click', $event, item)"
    >
        <template v-slot:title>
            <slot name="title"> Messages Sent per Resource </slot>
            &rarr; Top {{ dataComputed?.length || 10 }}
        </template>
        <template v-slot:subtitle>
            <slot name="subtitle"></slot>
        </template>

        <template v-slot:item-title="{ item }">
            <resource-details-popover
                v-if="shouldShowResourceDetails(item)"
                class="align-middle [&>div:first-child]:truncate w-full"
                :teamId="teamId"
                :orgUnitId="props.orgUnitId"
                :groupId="props.groupId"
                :modelValue="{
                    id: item.resource_id,
                    name: item.resource_name,
                    resource_type: item.resource_type,
                }"
                nameIcon
                hideSendButton
            >
                {{ item.title }}
            </resource-details-popover>
            <vx-popover v-else hover placement="top" arrow class="align-middle [&>div:first-child]:truncate w-full">
                {{ item.title }}

                <template v-slot:content>
                    <div class="flex items-center max-w-lg overflow-hidden">
                        <font-awesome-icon
                            widthAuto
                            :icon="resourceTypeIcon(item.resource_type)"
                            class="mr-2 text-slate-500 text-sm"
                        ></font-awesome-icon>
                        <span class="font-bold text-slate-700 text-lg truncate">{{ item.title }}</span>
                        <vx-badge size="xs" color="slate" class="ml-2">{{ item.resource_id }}</vx-badge>
                    </div>
                </template>
            </vx-popover>
        </template>
        <template v-slot:item-icons="{ item }">
            <font-awesome-icon
                v-if="FEATURES.contact_groups && item.sender_type"
                fixedWidth
                :icon="getSenderTypeIcon(item.sender_type, 'regular')"
                :class="getSenderTypeColorClass(item.sender_type)"
            ></font-awesome-icon>
            <font-awesome-icon fixedWidth :icon="resourceTypeIcon(item.resource_type, 'regular')"></font-awesome-icon>
        </template>

        <template v-slot:explainHeader>
            <slot name="explainHeader">Messages Sent per Resource</slot>
        </template>
        <template v-slot:explainContent>
            <slot name="explainContent">
                <p>
                    <strong>Messages Sent</strong><br />
                    Most relevant Voxie Resources in terms of Gross Count of Messages sent during the selected period.
                </p>
            </slot>
        </template>
    </vx-chart-record>
</template>

<script setup>
import { VxBadge, VxChartRecord, VxPopover } from '@voxie/frontend-components';
import { outboundMessagesAnalyticsClient } from '~/services';
import { watch, inject, computed } from 'vue';
import { useVersionedRequest } from '~/composables/useVersionedRequest';
import {
    metricsInputValidator,
    resourceTypeIcon,
    shouldShowResourceDetails,
    getSenderTypeIcon,
    getSenderTypeColorClass,
} from '~/utils/analytics';
import { stringifyQueryString } from '~/utils/string';
import ResourceDetailsPopover from '~/components/general/resources/ResourceDetailsPopover.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const FEATURES = inject('FEATURES');

const props = defineProps({
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
});

const emit = defineEmits(['resource:click']);

const teamId = Spark.state.currentTeam.id;

const { loading, response, error, fetchData } = useVersionedRequest();

const dataComputed = computed(() => {
    return (response.value?.data?.data || []).map((item) => {
        const queryString = {
            ...props.fetchParams,
            filters: {
                ...props.fetchParams.filters,
                resources: [
                    {
                        resource_type: item.resource_type,
                        resource_id: item.resource_id,
                    },
                ],
            },
        };
        return {
            ...item,
            href: '/analytics/outbound-messages?' + stringifyQueryString(queryString),
            key: item.resource_id,
            title: item.resource_name,
            value: item.values[0],
        };
    });
});

watch(
    () => props.fetchParams,
    () => {
        fetchData(
            outboundMessagesAnalyticsClient.getTotalMessagesSentByResource(
                {
                    teamId,
                    orgUnitId: props.orgUnitId,
                    groupId: props.groupId,
                },
                props.fetchParams
            )
        ).catch((e) => {
            console.error(e);
        });
    },
    { immediate: true, deep: true }
);
</script>
